// components/AssignmentProgressDialog.js
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { 
  CheckCircle, 
  RadioButtonUnchecked,
  Error,
  Schedule,
  Map,
  Assignment,
  LocationOn
} from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  dialog: {
    minWidth: 400
  },
  progressContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  progressBar: {
    height: 8,
    borderRadius: 4
  },
  stepList: {
    marginTop: theme.spacing(2)
  },
  stepItem: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1)
  },
  activeStep: {
    backgroundColor: theme.palette.action.hover
  },
  completedIcon: {
    color: theme.palette.success.main
  },
  activeIcon: {
    color: theme.palette.primary.main
  },
  pendingIcon: {
    color: theme.palette.text.disabled
  },
  errorIcon: {
    color: theme.palette.error.main
  },
  detailsBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`
  }
}));

const STEP_ICONS = {
  1: LocationOn,
  2: Assignment,
  3: Schedule,
  4: Map
};

const STEP_LABELS = {
  1: 'Removing from all routes',
  2: 'Updating agreement',
  3: 'Updating default routes',
  4: 'Updating future routes'
};

const AssignmentProgressDialog = ({ open, progress, onClose }) => {
  const classes = useStyles();
  
  console.log('📊 AssignmentProgressDialog - open:', open, 'progress:', progress);
  
  // Default values if no progress yet
  const { 
    step = 1, 
    totalSteps = 4, 
    message = 'Initializing...', 
    details = {}, 
    percentage = 0 
  } = progress || {};
  
  const isError = step === -1;
  
  const getStepIcon = (stepNumber) => {
    if (isError) {
      return <Error className={classes.errorIcon} />;
    } else if (step === 0) {
      // All steps pending when connecting
      return <RadioButtonUnchecked className={classes.pendingIcon} />;
    } else if (stepNumber < step) {
      return <CheckCircle className={classes.completedIcon} />;
    } else if (stepNumber === step) {
      return <CircularProgress size={24} className={classes.activeIcon} />;
    } else {
      return <RadioButtonUnchecked className={classes.pendingIcon} />;
    }
  };
  
  const getStepClass = (stepNumber) => {
    return stepNumber === step ? classes.activeStep : '';
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      disableBackdropClick
      disableEscapeKeyDown
      className={classes.dialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Assigning Site to Crew
      </DialogTitle>
      
      <DialogContent>
        <Box className={classes.progressContainer}>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="textSecondary">
              {message}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {percentage}%
            </Typography>
          </Box>
          
          <LinearProgress 
            variant="determinate" 
            value={isError ? 100 : percentage} 
            className={classes.progressBar}
            color={isError ? "secondary" : "primary"}
          />
        </Box>
        
        <List className={classes.stepList}>
          {[1, 2, 3, 4].map((stepNumber) => {
            const Icon = STEP_ICONS[stepNumber];
            return (
              <ListItem 
                key={stepNumber} 
                className={`${classes.stepItem} ${getStepClass(stepNumber)}`}
              >
                <ListItemIcon>
                  {getStepIcon(stepNumber)}
                </ListItemIcon>
                <ListItemText 
                  primary={STEP_LABELS[stepNumber]}
                  secondary={stepNumber === step ? message : null}
                />
              </ListItem>
            );
          })}
        </List>
        
        {details && (
          <Box className={classes.detailsBox}>
            <Typography variant="subtitle2" gutterBottom>
              Details
            </Typography>
            {details.frequency && (
              <Typography variant="body2" color="textSecondary">
                Frequency: {details.frequency}
              </Typography>
            )}
            {details.assignedDay !== undefined && (
              <Typography variant="body2" color="textSecondary">
                Assigned Day: {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][details.assignedDay]}
              </Typography>
            )}
            {details.crewName && (
              <Typography variant="body2" color="textSecondary">
                Crew: {details.crewName}
              </Typography>
            )}
            {details.routesUpdated !== undefined && (
              <Typography variant="body2" color="textSecondary">
                Routes Updated: {details.routesUpdated}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AssignmentProgressDialog;